import Footer from "../Footer/Footer";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useI18n } from "../../../i18n/I18nContext";

export default function OurTeam() {
  const { t } = useI18n();

  const pillars = [
    {
      number: "01",
      title: "Especialização técnica",
      text: "Profissionais com experiência em soluções de armazenagem, logística e otimização de espaço.",
    },
    {
      number: "02",
      title: "Trabalho colaborativo",
      text: "Atuamos lado a lado com clientes, parceiros e empresas para garantir decisões sólidas em cada projeto.",
    },
    {
      number: "03",
      title: "Soluções escaláveis",
      text: "Desenvolvemos propostas robustas, seguras e preparadas para acompanhar a evolução da operação.",
    },
  ];

  const highlights = [
    "Soluções de armazenagem",
    "Logística aplicada",
    "Otimização de espaço",
    "Projetos robustos e seguros",
  ];

  return (
    <div className="min-h-screen bg-stone-50 text-zinc-900 flex flex-col">
      <main className="flex-1 pt-20">
        <section className="border-b border-black/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-20">
            <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-end">
              <div className="lg:col-span-7">
                <div className="mb-6 flex items-center gap-3">
                  <span className="h-px w-10 bg-indigo-700" />
                  <span className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.22em] text-zinc-500">
                    {t("A nossa equipa")}
                  </span>
                </div>

                <h1 className="max-w-4xl text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[0.95] text-zinc-950">
                  {t("Uma equipa técnica")}
                  <span className="block text-indigo-700">
                    {t("com experiência")}
                  </span>
                </h1>
              </div>

              <div className="lg:col-span-5">
                <div className="rounded-[28px] border border-black/10 bg-white p-6 sm:p-7 shadow-[0_12px_40px_rgba(0,0,0,0.05)]">
                  <p className="text-sm font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-3">
                    Racksforall
                  </p>
                  <p className="text-base sm:text-lg leading-8 text-zinc-700">
                    {t("Na Racksforall contamos com profissionais especializados em soluções de armazenagem, logística e otimização de espaço.")}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-10 lg:mt-14 grid lg:grid-cols-12 gap-6 lg:gap-8">
              <div className="lg:col-span-8 rounded-[32px] bg-zinc-950 text-white p-7 sm:p-8 lg:p-10 relative overflow-hidden">
                <div className="absolute inset-0 opacity-[0.08]">
                  <div className="h-full w-full bg-[linear-gradient(to_right,white_1px,transparent_1px),linear-gradient(to_bottom,white_1px,transparent_1px)] bg-[size:32px_32px]" />
                </div>

                <div className="relative">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/60 mb-4">
                    {t("Como trabalhamos")}
                  </p>
                  <p className="max-w-3xl text-xl sm:text-2xl lg:text-3xl leading-tight font-semibold">
                    {t("Trabalhamos lado a lado com clientes, parceiros e empresas para desenvolver soluções robustas, seguras e escaláveis.")}
                  </p>

                  <div className="mt-8 flex flex-wrap gap-3">
                    <Link
                      to="/contact"
                      className="inline-flex items-center justify-center rounded-full bg-indigo-600 px-5 py-3 text-sm font-semibold text-white hover:bg-indigo-700 transition-colors"
                    >
                      {t("Falar com a equipa")}
                    </Link>

                    <Link
                      to="/products"
                      className="inline-flex items-center justify-center rounded-full border border-white/15 px-5 py-3 text-sm font-semibold text-white hover:bg-white/5 transition-colors"
                    >
                      {t("Ver Produtos")}
                    </Link>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-4 rounded-[32px] border border-black/10 bg-stone-100 p-6 sm:p-7 flex flex-col justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-4">
                    {t("Foco")}
                  </p>
                  <div className="space-y-3">
                    {highlights.map((item) => (
                      <div
                        key={item}
                        className="border-b border-black/10 pb-3 last:border-b-0 last:pb-0"
                      >
                        <p className="text-base font-semibold text-zinc-800">
                          {t(item)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-black/10">
                  <p className="text-4xl font-black tracking-tight text-zinc-950">
                    {t("Técnica.")}
                  </p>
                  <p className="text-sm text-zinc-600 mt-2">
                    {t("Próxima do cliente e orientada para resultados reais.")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-14 sm:py-16 lg:py-20">
            <div className="grid lg:grid-cols-12 gap-8 lg:gap-10">
              <div className="lg:col-span-4">
                <div className="lg:sticky lg:top-28">
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-zinc-500 mb-4">
                    {t("O que nos define")}
                  </p>
                  <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-zinc-950">
                    {t("Competência, proximidade e visão operacional.")}
                  </h2>
                  <p className="mt-4 text-zinc-600 leading-7">
                    {t("Uma abordagem técnica não tem de ser distante. A nossa equipa combina conhecimento especializado com acompanhamento próximo em cada fase do projeto.")}
                  </p>
                </div>
              </div>

              <div className="lg:col-span-8">
                <div className="space-y-4">
                  {pillars.map((item) => (
                    <article
                      key={item.number}
                      className="group grid md:grid-cols-[88px_1fr] gap-4 sm:gap-6 rounded-[28px] border border-black/10 bg-stone-50 px-5 sm:px-7 py-6 sm:py-7 hover:bg-stone-100 transition-colors"
                    >
                      <div className="flex items-start">
                        <span className="text-3xl sm:text-4xl font-black tracking-tight text-zinc-300 group-hover:text-indigo-700 transition-colors">
                          {item.number}
                        </span>
                      </div>

                      <div>
                        <h3 className="text-xl sm:text-2xl font-bold tracking-tight text-zinc-950">
                          {t(item.title)}
                        </h3>
                        <p className="mt-3 text-zinc-600 leading-7 max-w-2xl">
                          {t(item.text)}
                        </p>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-black/10 bg-stone-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-14">
            <div className="grid md:grid-cols-3 gap-px overflow-hidden rounded-[28px] border border-black/10 bg-black/10">
              <div className="bg-white p-6 sm:p-7">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-3">
                  {t("Armazenagem")}
                </p>
                <p className="text-lg font-semibold leading-8 text-zinc-900">
                  {t("Soluções pensadas para responder a diferentes exigências de capacidade, segurança e organização.")}
                </p>
              </div>

              <div className="bg-white p-6 sm:p-7">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-3">
                  {t("Logística")}
                </p>
                <p className="text-lg font-semibold leading-8 text-zinc-900">
                  {t("Conhecimento prático para alinhar eficiência operacional com a realidade de cada espaço.")}
                </p>
              </div>

              <div className="bg-zinc-950 p-6 sm:p-7 text-white">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/50 mb-3">
                  {t("Escala")}
                </p>
                <p className="text-lg font-semibold leading-8 text-white">
                  {t("Projetos robustos e escaláveis, preparados para crescer com o negócio do cliente.")}
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-stone-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-14 sm:py-16 lg:py-20">
            <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
              <div className="lg:col-span-7">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-zinc-500 mb-4">
                  {t("Trabalhar connosco")}
                </p>
                <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-zinc-950">
                  {t("Uma equipa preparada para transformar espaço em desempenho.")}
                </h2>
                <p className="mt-5 text-zinc-600 leading-8 max-w-3xl">
                  {t("Se procura um parceiro com experiência técnica, visão prática e compromisso com soluções seguras, a Racksforall está pronta para apoiar o seu projeto.")}
                </p>
              </div>

              <div className="lg:col-span-5">
                <div className="rounded-[30px] border border-black/10 bg-white p-6 sm:p-8 shadow-[0_12px_40px_rgba(0,0,0,0.05)]">
                  <div className="flex items-center justify-between gap-4 border-b border-black/10 pb-4">
                    <span className="text-sm font-semibold text-zinc-500">
                      {t("Próximo passo")}
                    </span>
                    <span className="text-sm font-semibold text-indigo-700">
                      {t("Contacto direto")}
                    </span>
                  </div>

                  <p className="mt-5 text-zinc-700 leading-7">
                    {t("Fale connosco para avaliar necessidades, discutir soluções e encontrar a melhor resposta para o seu espaço.")}
                  </p>

                  <div className="mt-6 flex flex-col sm:flex-row gap-3">
                    <Link
                      to="/contact"
                      className="inline-flex items-center justify-center rounded-full bg-zinc-950 px-5 py-3 text-sm font-semibold text-white hover:bg-zinc-800 transition-colors"
                    >
                      {t("Pedir contacto")}
                    </Link>

                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}